import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { adminAuthHeaders, fetchWithAdminAuth } from '@/lib/admin-auth'
import { API_URL } from '@/lib/constants'
import { useI18n } from '@/lib/i18n'
import { trpcClient } from '@/lib/trpc'
import { LANGUAGE_ENUM_VALUES } from '@repo/sdk'
import { createTRPCClient, httpSubscriptionLink } from '@trpc/client'
import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from 'api/src/types'
import {
  Loader2,
  MessageSquareText,
  RefreshCw,
  Search,
  Send,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import SuperJSON from 'superjson'

type RouterOutputs = inferRouterOutputs<AppRouter>
type Language = (typeof LANGUAGE_ENUM_VALUES)[number]
type Campaign
  = RouterOutputs['adminDirectMessagesRouter']['listCampaigns']['campaigns'][number]
type Recipient
  = RouterOutputs['adminDirectMessagesRouter']['searchRecipients']['users'][number]
type SocialMessage
  = RouterOutputs['socialRouter']['getMessages']['messages'][number]
type Conversation = { recipient: Recipient, connectionId: string }

const LANGUAGE_LABELS: Record<Language, string> = {
  zh: '🇨🇳 简体中文',
  tw: '🇹🇼 繁體中文',
  en: '🇬🇧 English',
  fr: '🇫🇷 Français',
  es: '🇪🇸 Español',
  ja: '🇯🇵 日本語',
  ko: '🇰🇷 한국어',
  ar: '🇸🇦 العربية',
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback
}

function interpolate(
  template: string,
  values: Record<string, string | number>,
): string {
  return template.replaceAll(/\{(\w+)\}/g, (match, key: string) =>
    String(values[key] ?? match))
}

function mergeMessages(
  previous: SocialMessage[],
  next: SocialMessage[],
): SocialMessage[] {
  const bySequence = new Map<number, SocialMessage>()
  for (const message of previous) bySequence.set(message.seq, message)
  for (const message of next) bySequence.set(message.seq, message)
  return [...bySequence.values()].sort((left, right) => left.seq - right.seq)
}

function newClientMessageId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    return crypto.randomUUID()
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

class AuthorizedEventSource {
  readonly CLOSED = 2
  readonly CONNECTING = 0
  readonly OPEN = 1
  readyState = this.CONNECTING
  private readonly abortController = new AbortController()
  private readonly listeners = new Map<string, Set<(event: Event) => void>>()

  constructor(private readonly url: string) {
    void this.connect()
  }

  addEventListener(type: string, listener: (event: Event) => void): void {
    const listeners = this.listeners.get(type) ?? new Set<(event: Event) => void>()
    listeners.add(listener)
    this.listeners.set(type, listeners)
  }

  removeEventListener(type: string, listener: (event: Event) => void): void {
    this.listeners.get(type)?.delete(listener)
  }

  close(): void {
    this.readyState = this.CLOSED
    this.abortController.abort()
    this.listeners.clear()
  }

  private emit(event: Event): void {
    for (const listener of this.listeners.get(event.type) ?? [])
      listener(event)
  }

  private emitFrame(eventType: string, eventData: string[], eventId: string): void {
    if (eventData.length === 0)
      return
    this.emit(new MessageEvent(eventType, {
      data: eventData.join('\n'),
      lastEventId: eventId,
    }))
  }

  private async readStream(response: Response): Promise<void> {
    const reader = response.body?.getReader()
    if (!reader)
      throw new Error('The event stream is unavailable.')
    const decoder = new TextDecoder()
    let buffer = ''
    let eventType = 'message'
    let eventData: string[] = []
    let eventId = ''
    try {
      while (!this.abortController.signal.aborted) {
        const chunk = await reader.read()
        if (chunk.done)
          return
        buffer += decoder.decode(chunk.value, { stream: true })
        let newline = buffer.indexOf('\n')
        while (newline !== -1) {
          const line = buffer.slice(0, newline).replace(/\r$/, '')
          buffer = buffer.slice(newline + 1)
          if (line === '') {
            this.emitFrame(eventType, eventData, eventId)
            eventType = 'message'
            eventData = []
          }
          else if (line.startsWith('event:')) {
            eventType = line.slice(6).trimStart()
          }
          else if (line.startsWith('data:')) {
            eventData.push(line.slice(5).trimStart())
          }
          else if (line.startsWith('id:')) {
            eventId = line.slice(3).trimStart()
          }
          newline = buffer.indexOf('\n')
        }
      }
    }
    finally {
      reader.releaseLock()
    }
  }

  private async connect(): Promise<void> {
    let retryDelay = 3000
    while (!this.abortController.signal.aborted) {
      try {
        const response = await fetchWithAdminAuth(this.url, {
          headers: { ...adminAuthHeaders(), accept: 'text/event-stream' },
          signal: this.abortController.signal,
        })
        if (response.status === 401 || response.status === 403) {
          this.readyState = this.CLOSED
          this.emit(new Event('error'))
          return
        }
        if (!response.ok)
          throw new Error(`Event stream failed (HTTP ${response.status}).`)
        this.readyState = this.OPEN
        retryDelay = 3000
        await this.readStream(response)
      }
      catch {
        if (this.abortController.signal.aborted)
          return
      }
      if (this.abortController.signal.aborted)
        return
      this.readyState = this.CONNECTING
      this.emit(new Event('error'))
      const waitMs = retryDelay
      await new Promise<void>((resolve) => {
        const timer = window.setTimeout(resolve, waitMs)
        this.abortController.signal.addEventListener('abort', () => {
          window.clearTimeout(timer)
          resolve()
        }, { once: true })
      })
      retryDelay = Math.min(retryDelay * 2, 30000)
    }
  }
}

const realtimeClient = createTRPCClient<AppRouter>({
  links: [httpSubscriptionLink({
    url: `${API_URL}/api`,
    transformer: SuperJSON,
    EventSource: AuthorizedEventSource,
  })],
})

export function AdminDirectMessagesSection() {
  const { language, t } = useI18n()
  const [targetLanguage, setTargetLanguage] = useState<Language>('en')
  const [bulkText, setBulkText] = useState('')
  const [preview, setPreview] = useState<{
    language: Language
    count: number
  } | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [confirmation, setConfirmation] = useState<{
    language: Language
    text: string
    count: number
    clientCampaignId: string
  } | null>(null)
  const [sendingCampaign, setSendingCampaign] = useState(false)
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [campaignsLoading, setCampaignsLoading] = useState(false)
  const [campaignError, setCampaignError] = useState('')
  const [campaignNotice, setCampaignNotice] = useState('')
  const [historyError, setHistoryError] = useState('')
  const [historyNotice, setHistoryNotice] = useState('')
  const [historyLastUpdatedAt, setHistoryLastUpdatedAt] = useState<
    number | null
  >(null)
  const [historyStale, setHistoryStale] = useState(false)
  const [retryingCampaignId, setRetryingCampaignId] = useState<string | null>(
    null,
  )
  const [recipientQuery, setRecipientQuery] = useState('')
  const [recipients, setRecipients] = useState<Recipient[]>([])
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState('')
  const [openingUserId, setOpeningUserId] = useState<string | null>(null)
  const [conversation, setConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<SocialMessage[]>([])
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [olderLoading, setOlderLoading] = useState(false)
  const [hasOlderMessages, setHasOlderMessages] = useState(false)
  const [chatText, setChatText] = useState('')
  const [chatSending, setChatSending] = useState(false)
  const [chatError, setChatError] = useState('')
  const [liveConnected, setLiveConnected] = useState(false)
  const activeConnectionId = useRef<string | null>(null)
  const readThrough = useRef<{ connectionId: string, seq: number } | null>(null)
  const sendAttempt = useRef<{
    connectionId: string
    text: string
    id: string
  } | null>(null)
  const campaignAttempt = useRef<{
    language: Language
    text: string
    id: string
  } | null>(null)
  const loadedOlderMessages = useRef(false)
  const connectionId = conversation?.connectionId

  const loadCampaigns = useCallback(async () => {
    setCampaignsLoading(true)
    try {
      const result
        = await trpcClient.adminDirectMessagesRouter.listCampaigns.query({
          limit: 20,
        })
      setCampaigns(result.campaigns)
      setHistoryError('')
      setHistoryStale(false)
      setHistoryLastUpdatedAt(Date.now())
    }
    catch (error) {
      setHistoryError(
        errorMessage(error, t('admin.directMessages.unexpectedError')),
      )
      setHistoryStale(true)
    }
    finally {
      setCampaignsLoading(false)
    }
  }, [t])

  useEffect(() => {
    void loadCampaigns()
  }, [loadCampaigns])

  useEffect(() => {
    if (
      !campaigns.some(
        campaign =>
          campaign.status === 'queued' || campaign.status === 'running',
      )
    ) {
      return
    }
    const timer = window.setInterval(() => {
      void loadCampaigns()
    }, 5000)
    return () => window.clearInterval(timer)
  }, [campaigns, loadCampaigns])

  const previewAudience = async () => {
    setPreviewing(true)
    setCampaignError('')
    setCampaignNotice('')
    setPreview(null)
    try {
      const result
        = await trpcClient.adminDirectMessagesRouter.previewAudience.mutate({
          targetLanguage,
        })
      setPreview({ language: targetLanguage, count: result.count })
    }
    catch (error) {
      setCampaignError(
        errorMessage(error, t('admin.directMessages.unexpectedError')),
      )
    }
    finally {
      setPreviewing(false)
    }
  }

  const requestCampaign = () => {
    const text = bulkText.trim()
    if (!text || preview?.language !== targetLanguage || preview.count === 0)
      return
    const attempt
      = campaignAttempt.current?.language === targetLanguage
        && campaignAttempt.current.text === text
        ? campaignAttempt.current
        : { language: targetLanguage, text, id: crypto.randomUUID() }
    campaignAttempt.current = attempt
    setConfirmation({
      language: targetLanguage,
      text,
      count: preview.count,
      clientCampaignId: attempt.id,
    })
  }

  const createCampaign = async () => {
    if (!confirmation)
      return
    const request = confirmation
    setConfirmation(null)
    setSendingCampaign(true)
    setCampaignError('')
    setCampaignNotice('')
    try {
      const result
        = await trpcClient.adminDirectMessagesRouter.createCampaign.mutate({
          clientCampaignId: request.clientCampaignId,
          targetLanguage: request.language,
          expectedRecipientCount: request.count,
          text: request.text,
        })
      if (result.status === 'failed') {
        setCampaignError(t('admin.directMessages.campaignQueueFailed'))
      }
      else {
        setCampaignNotice(
          interpolate(t('admin.directMessages.campaignAccepted'), {
            count: result.recipientCount,
          }),
        )
        setBulkText('')
        setPreview(null)
        campaignAttempt.current = null
      }
      await loadCampaigns()
    }
    catch (error) {
      const audienceChanged
        = error instanceof Error && error.message.includes('campaign_audience_changed')
      setCampaignError(
        audienceChanged
          ? t('admin.directMessages.audienceChanged')
          : errorMessage(error, t('admin.directMessages.unexpectedError')),
      )
      if (audienceChanged)
        setPreview(null)
    }
    finally {
      setSendingCampaign(false)
    }
  }

  const refreshCampaign = async (campaignId: string) => {
    setHistoryError('')
    try {
      const refreshed
        = await trpcClient.adminDirectMessagesRouter.getCampaign.query({
          campaignId,
        })
      setCampaigns(previous =>
        previous.map(campaign =>
          campaign.id === refreshed.id ? refreshed : campaign,
        ),
      )
    }
    catch (error) {
      setHistoryError(
        errorMessage(error, t('admin.directMessages.unexpectedError')),
      )
      setHistoryStale(true)
    }
  }

  const retryCampaign = async (campaignId: string) => {
    if (retryingCampaignId !== null)
      return
    setRetryingCampaignId(campaignId)
    setHistoryError('')
    setHistoryNotice('')
    try {
      const result
        = await trpcClient.adminDirectMessagesRouter.retryCampaign.mutate({
          campaignId,
        })
      if (!result.success) {
        setHistoryError(t('admin.directMessages.retryUnavailable'))
        return
      }
      setHistoryNotice(t('admin.directMessages.retryQueued'))
      await loadCampaigns()
    }
    catch (error) {
      setHistoryError(
        errorMessage(error, t('admin.directMessages.unexpectedError')),
      )
      setHistoryStale(true)
    }
    finally {
      setRetryingCampaignId(null)
    }
  }

  const searchRecipients = async () => {
    const query = recipientQuery.trim()
    if (query.length < 2)
      return
    setSearching(true)
    setSearchError('')
    try {
      const result
        = await trpcClient.adminDirectMessagesRouter.searchRecipients.query({
          query,
          limit: 20,
        })
      setRecipients(result.users)
    }
    catch (error) {
      setSearchError(
        errorMessage(error, t('admin.directMessages.unexpectedError')),
      )
    }
    finally {
      setSearching(false)
    }
  }

  const openConversation = async (recipient: Recipient) => {
    setOpeningUserId(recipient.id)
    setChatError('')
    try {
      const result
        = await trpcClient.adminDirectMessagesRouter.openConversation.mutate({
          targetUserId: recipient.id,
        })
      activeConnectionId.current = result.connectionId
      readThrough.current = null
      sendAttempt.current = null
      loadedOlderMessages.current = false
      setMessages([])
      setChatText('')
      setHasOlderMessages(false)
      setConversation({ recipient, connectionId: result.connectionId })
    }
    catch (error) {
      setChatError(
        errorMessage(error, t('admin.directMessages.unexpectedError')),
      )
    }
    finally {
      setOpeningUserId(null)
    }
  }

  useEffect(() => {
    if (!connectionId)
      return
    let cancelled = false
    let live = false
    let pollCount = 0
    const loadLatest = async () => {
      try {
        const page = await trpcClient.socialRouter.getMessages.query({
          connectionId,
          limit: 50,
        })
        if (cancelled || activeConnectionId.current !== connectionId)
          return
        setMessages(previous => mergeMessages(previous, page.messages))
        if (!loadedOlderMessages.current)
          setHasOlderMessages(page.hasMore)
        const newestSeq = page.messages.at(-1)?.seq ?? 0
        if (
          newestSeq
          > (readThrough.current?.connectionId === connectionId
            ? readThrough.current.seq
            : 0)
        ) {
          readThrough.current = { connectionId, seq: newestSeq }
          void trpcClient.socialRouter.markRead
            .mutate({ connectionId, throughSeq: newestSeq })
            .catch(() => {
              if (
                readThrough.current?.connectionId === connectionId
                && readThrough.current.seq === newestSeq
              ) {
                readThrough.current = null
              }
            })
        }
        setChatError('')
      }
      catch (error) {
        if (!cancelled && activeConnectionId.current === connectionId) {
          setChatError(
            errorMessage(error, t('admin.directMessages.unexpectedError')),
          )
        }
      }
      finally {
        if (!cancelled && activeConnectionId.current === connectionId)
          setMessagesLoading(false)
      }
    }

    setMessagesLoading(true)
    setLiveConnected(false)
    void loadLatest()
    const subscription = realtimeClient.userEventsRouter.onUserChanged.subscribe(undefined, {
      onData: (signal) => {
        if (cancelled)
          return
        if (signal.type === 'stream.resync'
          || (signal.type === 'social.message' && signal.connectionId === connectionId)) {
          void loadLatest()
        }
      },
      onConnectionStateChange: ({ state }) => {
        live = state === 'pending'
        if (!cancelled)
          setLiveConnected(live)
        if (live)
          void loadLatest()
      },
      onError: () => {
        live = false
        if (!cancelled)
          setLiveConnected(false)
      },
    })
    const timer = window.setInterval(() => {
      pollCount += 1
      if (document.visibilityState === 'visible'
        && (!live || pollCount % 8 === 0)) {
        void loadLatest()
      }
    }, 4000)
    return () => {
      cancelled = true
      subscription.unsubscribe()
      window.clearInterval(timer)
    }
  }, [connectionId, t])

  const loadOlderMessages = async () => {
    if (!conversation || !hasOlderMessages || olderLoading)
      return
    const oldestSeq = messages[0]?.seq
    if (!oldestSeq)
      return
    const { connectionId } = conversation
    setOlderLoading(true)
    setChatError('')
    try {
      const page = await trpcClient.socialRouter.getMessages.query({
        connectionId,
        beforeSeq: oldestSeq,
        limit: 50,
      })
      if (activeConnectionId.current !== connectionId)
        return
      loadedOlderMessages.current = true
      setMessages(previous => mergeMessages(previous, page.messages))
      setHasOlderMessages(page.hasMore)
    }
    catch (error) {
      setChatError(
        errorMessage(error, t('admin.directMessages.unexpectedError')),
      )
    }
    finally {
      setOlderLoading(false)
    }
  }

  const clearCompletedSendAttempt = (id: string) => {
    if (sendAttempt.current?.id === id)
      sendAttempt.current = null
  }

  const sendChatMessage = async () => {
    if (!conversation || chatSending)
      return
    const text = chatText.trim()
    if (!text)
      return
    const { connectionId } = conversation
    const attempt
      = sendAttempt.current?.connectionId === connectionId
        && sendAttempt.current.text === text
        ? sendAttempt.current
        : { connectionId, text, id: newClientMessageId() }
    sendAttempt.current = attempt
    setChatSending(true)
    setChatError('')
    try {
      const message = await trpcClient.socialRouter.sendMessage.mutate({
        connectionId,
        clientMessageId: attempt.id,
        type: 'text',
        text,
      })
      if (activeConnectionId.current === connectionId) {
        setMessages(previous => mergeMessages(previous, [message]))
        setChatText(current => (current.trim() === text ? '' : current))
        clearCompletedSendAttempt(attempt.id)
      }
    }
    catch (error) {
      if (activeConnectionId.current === connectionId) {
        setChatError(
          errorMessage(error, t('admin.directMessages.unexpectedError')),
        )
      }
    }
    finally {
      setChatSending(false)
    }
  }

  return (
    <div className="container mx-auto max-w-6xl space-y-6 py-8">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquareText className="size-5" />
            {t('admin.directMessages.title')}
          </CardTitle>
          <CardDescription>
            {t('admin.directMessages.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label htmlFor="direct-message-language">
                {t('admin.directMessages.recipientLanguage')}
              </Label>
              <select
                id="direct-message-language"
                value={targetLanguage}
                onChange={(event) => {
                  setTargetLanguage(event.target.value as Language)
                  setPreview(null)
                  setCampaignNotice('')
                  campaignAttempt.current = null
                }}
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {LANGUAGE_ENUM_VALUES.map(language => (
                  <option key={language} value={language}>
                    {LANGUAGE_LABELS[language]}
                  </option>
                ))}
              </select>
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                void previewAudience()
              }}
              disabled={previewing || sendingCampaign}
            >
              {previewing ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <Search className="mr-2 size-4" />
              )}
              {t('admin.directMessages.previewRecipients')}
            </Button>
            {preview?.language === targetLanguage && (
              <span className="text-sm text-muted-foreground">
                {interpolate(t('admin.directMessages.recipientCount'), {
                  count: preview.count,
                })}
              </span>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="direct-message-bulk-text">
              {t('admin.directMessages.message')}
            </Label>
            <Textarea
              id="direct-message-bulk-text"
              value={bulkText}
              onChange={(event) => {
                setBulkText(event.target.value)
                campaignAttempt.current = null
              }}
              maxLength={2000}
              className="min-h-32"
              placeholder={t('admin.directMessages.messagePlaceholder')}
            />
            <p className="text-xs text-muted-foreground">
              {interpolate(t('admin.directMessages.characterCount'), {
                count: bulkText.length,
              })}
            </p>
          </div>
          <Button
            type="button"
            onClick={requestCampaign}
            disabled={sendingCampaign
              || !bulkText.trim()
              || preview?.language !== targetLanguage
              || preview.count === 0}
          >
            {sendingCampaign ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : (
              <Send className="mr-2 size-4" />
            )}
            {t('admin.directMessages.sendToLanguage')}
          </Button>
          {campaignNotice && (
            <p className="text-sm text-green-700 dark:text-green-400">
              {campaignNotice}
            </p>
          )}
          {campaignError && (
            <p className="text-sm text-red-600">{campaignError}</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>{t('admin.directMessages.sendHistory')}</CardTitle>
              <CardDescription>
                {t('admin.directMessages.sendHistoryDescription')}
              </CardDescription>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                void loadCampaigns()
              }}
              disabled={campaignsLoading}
            >
              {campaignsLoading ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 size-4" />
              )}
              {t('admin.directMessages.refresh')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            {t('admin.directMessages.progressExplanation')}
          </p>
          {historyLastUpdatedAt !== null && (
            <p className="text-xs text-muted-foreground">
              {interpolate(t('admin.directMessages.historyLastUpdated'), {
                time: new Date(historyLastUpdatedAt).toLocaleString(
                  language === 'zh' ? 'zh-CN' : 'en-US',
                ),
              })}
            </p>
          )}
          {historyStale && (
            <p
              className="text-sm text-amber-700 dark:text-amber-300"
              role="status"
            >
              {historyLastUpdatedAt === null
                ? t('admin.directMessages.historyUnavailable')
                : t('admin.directMessages.historyStale')}
            </p>
          )}
          {historyNotice && (
            <p className="text-sm text-green-700 dark:text-green-400">
              {historyNotice}
            </p>
          )}
          {historyError && (
            <p className="text-sm text-red-600">{historyError}</p>
          )}
          {campaigns.length === 0 && !campaignsLoading && (
            <p className="text-sm text-muted-foreground">
              {t('admin.directMessages.emptyCampaigns')}
            </p>
          )}
          {campaigns.map((campaign) => {
            const processed = campaign.deliveredCount + campaign.failedCount
            return (
              <div key={campaign.id} className="rounded-md border p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-medium">
                      {LANGUAGE_LABELS[campaign.targetLanguage]} ·{' '}
                      {t(`admin.directMessages.${campaign.status}`)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(campaign.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {campaign.status === 'failed' && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          void retryCampaign(campaign.id)
                        }}
                        disabled={retryingCampaignId !== null}
                      >
                        {retryingCampaignId === campaign.id && (
                          <Loader2 className="mr-2 size-4 animate-spin" />
                        )}
                        {t('admin.directMessages.retry')}
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        void refreshCampaign(campaign.id)
                      }}
                    >
                      {t('admin.directMessages.refresh')}
                    </Button>
                  </div>
                </div>
                <p className="mt-2 whitespace-pre-wrap break-words text-sm">
                  {campaign.text}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  {interpolate(t('admin.directMessages.progress'), {
                    processed,
                    total: campaign.recipientCount,
                    delivered: campaign.deliveredCount,
                    failed: campaign.failedCount,
                  })}
                </p>
                <progress
                  className="mt-2 h-2 w-full"
                  max={Math.max(campaign.recipientCount, 1)}
                  value={processed}
                />
              </div>
            )
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('admin.directMessages.chatTitle')}</CardTitle>
          <CardDescription>
            {t('admin.directMessages.chatDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <form
            className="flex flex-col gap-2 sm:flex-row"
            onSubmit={(event) => {
              event.preventDefault()
              void searchRecipients()
            }}
          >
            <Input
              value={recipientQuery}
              onChange={event => setRecipientQuery(event.target.value)}
              placeholder={t('admin.directMessages.searchPlaceholder')}
              aria-label={t('admin.directMessages.searchRecipients')}
            />
            <Button
              type="submit"
              variant="secondary"
              disabled={searching || recipientQuery.trim().length < 2}
            >
              {searching ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <Search className="mr-2 size-4" />
              )}
              {t('admin.directMessages.search')}
            </Button>
          </form>
          {searchError && <p className="text-sm text-red-600">{searchError}</p>}
          {recipients.length > 0 && (
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {recipients.map(recipient => (
                <div
                  key={recipient.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3"
                >
                  <div className="min-w-0">
                    <p className="font-medium">
                      {recipient.displayName
                        || recipient.username
                        || recipient.email}
                    </p>
                    <p className="break-all text-xs text-muted-foreground">
                      {recipient.email}
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      void openConversation(recipient)
                    }}
                    disabled={openingUserId !== null}
                  >
                    {openingUserId === recipient.id && (
                      <Loader2 className="mr-2 size-4 animate-spin" />
                    )}
                    {t('admin.directMessages.openChat')}
                  </Button>
                </div>
              ))}
            </div>
          )}
          {conversation && (
            <div className="space-y-3 rounded-md border p-4">
              <h3 className="font-semibold">
                {interpolate(t('admin.directMessages.chatWith'), {
                  name:
                    conversation.recipient.displayName
                    || conversation.recipient.username
                    || conversation.recipient.email,
                })}
              </h3>
              <p className="text-xs text-muted-foreground" role="status">
                {t(liveConnected
                  ? 'admin.directMessages.liveConnected'
                  : 'admin.directMessages.liveFallback')}
              </p>
              {messagesLoading && messages.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  {t('admin.directMessages.loadingMessages')}
                </p>
              )}
              {hasOlderMessages && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    void loadOlderMessages()
                  }}
                  disabled={olderLoading}
                >
                  {olderLoading && (
                    <Loader2 className="mr-2 size-4 animate-spin" />
                  )}
                  {t('admin.directMessages.loadEarlierMessages')}
                </Button>
              )}
              <div
                className="max-h-96 space-y-3 overflow-y-auto rounded-md bg-muted/30 p-3"
                aria-live="polite"
              >
                {messages.length === 0 && !messagesLoading && (
                  <p className="text-sm text-muted-foreground">
                    {t('admin.directMessages.emptyMessages')}
                  </p>
                )}
                {messages.map(message => (
                  <div
                    key={message.id}
                    className={`max-w-[85%] rounded-md p-3 text-sm ${message.senderUserId === conversation.recipient.id ? 'bg-background' : 'ml-auto bg-primary/10'}`}
                  >
                    <p className="mb-1 text-xs font-medium text-muted-foreground">
                      {message.senderUserId === conversation.recipient.id
                        ? conversation.recipient.displayName
                        || conversation.recipient.username
                        || conversation.recipient.email
                        : t('admin.directMessages.you')}
                    </p>
                    <p className="whitespace-pre-wrap break-words">
                      {message.text
                        ?? message.caption
                        ?? t('admin.directMessages.roomInvitation')}
                    </p>
                    {message.room && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {interpolate(t('admin.directMessages.room'), {
                          code: message.room.roomCode,
                        })}
                      </p>
                    )}
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {new Date(message.createdAt).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <Label htmlFor="direct-chat-text">
                  {t('admin.directMessages.reply')}
                </Label>
                <Textarea
                  id="direct-chat-text"
                  value={chatText}
                  onChange={event => setChatText(event.target.value)}
                  maxLength={2000}
                  placeholder={t('admin.directMessages.replyPlaceholder')}
                />
                <Button
                  type="button"
                  onClick={() => {
                    void sendChatMessage()
                  }}
                  disabled={chatSending || !chatText.trim()}
                >
                  {chatSending ? (
                    <Loader2 className="mr-2 size-4 animate-spin" />
                  ) : (
                    <Send className="mr-2 size-4" />
                  )}
                  {t('admin.directMessages.sendReply')}
                </Button>
                {chatError && (
                  <p className="text-sm text-red-600">{chatError}</p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {confirmation && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="direct-message-confirm-title"
            className="w-full max-w-lg space-y-4 rounded-lg border bg-background p-6 shadow-xl"
          >
            <h3
              id="direct-message-confirm-title"
              className="text-lg font-semibold"
            >
              {t('admin.directMessages.confirmTitle')}
            </h3>
            <p className="text-sm">
              {interpolate(t('admin.directMessages.confirmAudience'), {
                count: confirmation.count,
                language: LANGUAGE_LABELS[confirmation.language],
              })}
            </p>
            <pre className="max-h-56 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted p-3 text-sm">
              {confirmation.text}
            </pre>
            <p className="text-xs text-muted-foreground">
              {t('admin.directMessages.confirmDescription')}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmation(null)}
              >
                {t('admin.directMessages.cancel')}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  void createCampaign()
                }}
              >
                {t('admin.directMessages.confirmSend')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
